/**
 * WeekView - an adaptive multi-day schedule canvas.
 *
 * Every layout renders the full seven-day week. Compact phone lanes follow the
 * familiar Google Calendar pattern while wide/foldable screens naturally gain
 * more room. Grid lines, cards, and the scroll anchor share one UI-thread zoom
 * value, so pinching never scales text or requires a layout swap.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LayoutChangeEvent,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  type GestureResponderEvent,
  useWindowDimensions,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { DateTime } from 'luxon';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  runOnJS,
  scrollTo,
  useAnimatedRef,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSpring,
  withTiming,
  type SharedValue,
} from 'react-native-reanimated';

import type { CalendarEvent } from '../../types/api';
import { useTheme, type ThemeColors } from '../../utils/theme';
import { getScheduleEventColor, getScheduleEventFillColor } from './scheduleAppearance';
import {
  clampScheduleScale,
  getAnchoredScheduleScroll,
  getScheduleAutoFitColumnWidth,
  getScheduleColumnAtX,
  getScheduleTextProgress,
  resizeScheduleColumns,
} from './scheduleViewport';

const GOOGLE_BLUE = '#4285F4';
const DEFAULT_HOUR_HEIGHT = 60;
const MIN_HOUR_HEIGHT = 30;
const MAX_HOUR_HEIGHT = 150;
const HEADER_HEIGHT = 56;
const HOUR_LABEL_WIDTH = 50;
const CONTENT_BOTTOM_PADDING = 32;
const DAYS_PER_WEEK = 7;
const EVENT_GAP = 2;
const EVENT_INSET = 3;
const EVENT_MIN_HEIGHT = 24;
const SNAP_MINUTES = 15;
const HOLD_DRIFT_CANCEL_PX = 22;
const EDGE_ZONE = 56;
const SCROLL_STEP = 8;
const MIN_DAY_COLUMN_WIDTH = 28;

const AnimatedScrollView = Animated.createAnimatedComponent(ScrollView);

interface Props {
  events: CalendarEvent[];
  selectedDate: string;
  onSelectDate: (date: string) => void;
  onPressEvent?: (event: CalendarEvent) => void;
  onOpenDay?: (date: string) => void;
  onResizeEvent?: (seq: number, durationMinutes: number) => void | Promise<void>;
  onMoveEvent?: (seq: number, newRunAtUtc: string) => void | Promise<void>;
  onHorizontalOverflowChange?: (hasOverflow: boolean) => void;
  onNavigateWeek?: (direction: -1 | 1) => void;
  tagColors?: Record<string, string> | null;
  zoomGestureActive?: SharedValue<number>;
}

type PositionedWeekEvent = {
  event: CalendarEvent;
  startMinutes: number;
  durationMinutes: number;
  lane: number;
  laneCount: number;
  groupEvents: CalendarEvent[];
};

interface WeekEventBlockProps {
  item: PositionedWeekEvent;
  dayIndex: number;
  colWidth: number;
  columnLeft: number;
  columnWidths: number[];
  dense: boolean;
  isSelected: boolean;
  hourHeight: SharedValue<number>;
  scrollOffset: SharedValue<number>;
  scrollNativeGesture: ReturnType<typeof Gesture.Native>;
  horizontalScrollNativeGesture: ReturnType<typeof Gesture.Native>;
  styles: ReturnType<typeof createStyles>;
  tagColors?: Record<string, string> | null;
  onPress?: (event: CalendarEvent) => void;
  onSelect: (event: CalendarEvent) => void;
  onResize?: (event: CalendarEvent, durationMinutes: number) => void;
  onPressGroup: () => void;
  onDragStart: (scrollOffset: number) => void;
  onDragPreview: (dayIndex: number, minutes: number) => void;
  onDragEdge: (absoluteY: number) => void;
  onDragStop: () => void;
  onDrop: (event: CalendarEvent, dayIndex: number, minutes: number) => void;
}

interface ColumnResizeHandleProps {
  columnIndex: number;
  styles: ReturnType<typeof createStyles>;
  active: boolean;
  atTrailingEdge: boolean;
  horizontalScrollNativeGesture: ReturnType<typeof Gesture.Native>;
  onResizeStart: (columnIndex: number) => void;
  onResize: (columnIndex: number, translationX: number) => void;
  onResizeEnd: () => void;
  onAutoFit: (columnIndex: number) => void;
}

function clamp(value: number, min: number, max: number) {
  'worklet';
  return Math.max(min, Math.min(max, value));
}

function getWeekDays(selectedDate: string) {
  const selected = DateTime.fromISO(selectedDate);
  const weekStart = (selected.isValid ? selected : DateTime.local()).startOf('week');
  return Array.from({ length: 7 }, (_, index) => weekStart.plus({ days: index }));
}

function minutesOfDay(isoUtc: string): number {
  const date = DateTime.fromISO(isoUtc).toLocal();
  return date.hour * 60 + date.minute;
}

function formatMinutes(minutes: number) {
  return DateTime.local().startOf('day').plus({ minutes }).toFormat('h:mm a');
}

function formatHour(hour: number) {
  if (hour === 0) return '12 AM';
  if (hour < 12) return `${hour} AM`;
  if (hour === 12) return '12 PM';
  return `${hour - 12} PM`;
}

function eventSelectionKey(event: CalendarEvent): string {
  if (event.seq != null) return `seq:${event.seq}`;
  if (event.googleEventId) return `google:${event.googleEventId}`;
  return `${event.runAtUtc}:${event.body}`;
}

function ColumnResizeHandle({
  columnIndex,
  styles,
  active,
  atTrailingEdge,
  horizontalScrollNativeGesture,
  onResizeStart,
  onResize,
  onResizeEnd,
  onAutoFit,
}: ColumnResizeHandleProps) {
  const resizeGesture = useMemo(() => Gesture.Pan()
    .activeOffsetX([-2, 2])
    .failOffsetY([-14, 14])
    .blocksExternalGesture(horizontalScrollNativeGesture)
    .onStart(() => {
      runOnJS(onResizeStart)(columnIndex);
    })
    .onUpdate((event) => {
      runOnJS(onResize)(columnIndex, event.translationX);
    })
    .onFinalize(() => {
      runOnJS(onResizeEnd)();
    }), [columnIndex, horizontalScrollNativeGesture, onResize, onResizeEnd, onResizeStart]);

  const autoFitGesture = useMemo(() => Gesture.Tap()
    .numberOfTaps(2)
    .maxDuration(260)
    .onEnd((_event, success) => {
      if (success) runOnJS(onAutoFit)(columnIndex);
    }), [columnIndex, onAutoFit]);

  const handleGesture = useMemo(
    () => Gesture.Race(resizeGesture, autoFitGesture),
    [autoFitGesture, resizeGesture],
  );

  return (
    <GestureDetector gesture={handleGesture}>
      <View
        accessible
        accessibilityRole="adjustable"
        accessibilityLabel="Resize day column. Double tap to auto fit."
        style={[
          styles.columnResizeHitArea,
          active && styles.columnResizeHitAreaActive,
          atTrailingEdge && styles.columnResizeHitAreaTrailing,
        ]}
      >
        <View style={[styles.columnResizeLine, atTrailingEdge && styles.columnResizeLineTrailing]} />
        <View style={styles.columnResizeKnob}>
          <Ionicons name="reorder-two" size={12} color="#FFFFFF" />
        </View>
      </View>
    </GestureDetector>
  );
}

function positionDayEvents(events: CalendarEvent[]): PositionedWeekEvent[] {
  const rawEvents = events
    .filter((event) => !!event.runAtUtc)
    .map((event) => {
      const startMinutes = minutesOfDay(event.runAtUtc);
      const durationMinutes = Math.max(SNAP_MINUTES, event.durationMinutes || 30);
      return { event, startMinutes, durationMinutes, groupEvents: [event] };
    })
    .sort((left, right) => left.startMinutes - right.startMinutes);

  const starts = new Map<number, typeof rawEvents[number]>();
  rawEvents.forEach((item) => {
    const existing = starts.get(item.startMinutes);
    if (!existing) {
      starts.set(item.startMinutes, item);
      return;
    }
    existing.groupEvents.push(item.event);
    existing.durationMinutes = Math.max(existing.durationMinutes, item.durationMinutes);
  });
  const ordered = Array.from(starts.values());

  const result: PositionedWeekEvent[] = [];
  let cluster: typeof ordered = [];
  let clusterEnd = -1;

  const flushCluster = () => {
    if (cluster.length === 0) return;
    const laneEnds: number[] = [];
    const placed = cluster.map((item) => {
      let lane = laneEnds.findIndex((end) => end <= item.startMinutes);
      if (lane < 0) lane = laneEnds.length;
      laneEnds[lane] = item.startMinutes + item.durationMinutes;
      return { ...item, lane };
    });
    const laneCount = Math.max(1, laneEnds.length);
    placed.forEach((item) => result.push({ ...item, laneCount }));
    cluster = [];
    clusterEnd = -1;
  };

  ordered.forEach((item) => {
    if (cluster.length > 0 && item.startMinutes >= clusterEnd) flushCluster();
    cluster.push(item);
    clusterEnd = Math.max(clusterEnd, item.startMinutes + item.durationMinutes);
  });
  flushCluster();
  return result;
}

const HourMarker = React.memo(({
  hour,
  hourHeight,
  styles,
}: {
  hour: number;
  hourHeight: SharedValue<number>;
  styles: ReturnType<typeof createStyles>;
}) => {
  const positionStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: hour * hourHeight.value }],
  }));

  return (
    <Animated.View style={[styles.hourMarker, positionStyle]}>
      <Text style={styles.hourText}>{formatHour(hour)}</Text>
      <View style={styles.hourLine} />
    </Animated.View>
  );
});

const FixedHourLabel = React.memo(({
  hour,
  hourHeight,
  styles,
}: {
  hour: number;
  hourHeight: SharedValue<number>;
  styles: ReturnType<typeof createStyles>;
}) => {
  const positionStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: hour * hourHeight.value }],
  }));

  return (
    <Animated.View style={[styles.fixedHourLabel, positionStyle]}>
      <Text style={styles.fixedHourLabelText}>{formatHour(hour)}</Text>
    </Animated.View>
  );
});

function NowLine({ hourHeight, styles }: {
  hourHeight: SharedValue<number>;
  styles: ReturnType<typeof createStyles>;
}) {
  const [nowMinutes, setNowMinutes] = useState(() => {
    const now = DateTime.local();
    return now.hour * 60 + now.minute;
  });

  useEffect(() => {
    const timer = setInterval(() => {
      const now = DateTime.local();
      setNowMinutes(now.hour * 60 + now.minute);
    }, 60_000);
    return () => clearInterval(timer);
  }, []);

  const positionStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: (nowMinutes / 60) * hourHeight.value }],
  }));

  return (
    <Animated.View style={[styles.nowLineWrap, positionStyle]} pointerEvents="none">
      <View style={styles.nowDot} />
      <View style={styles.nowLine} />
    </Animated.View>
  );
}

function WeekEventBlock({
  item,
  dayIndex,
  colWidth,
  columnLeft,
  columnWidths,
  dense,
  isSelected,
  hourHeight,
  scrollOffset,
  scrollNativeGesture,
  horizontalScrollNativeGesture,
  styles,
  tagColors,
  onPress,
  onSelect,
  onResize,
  onPressGroup,
  onDragStart,
  onDragPreview,
  onDragEdge,
  onDragStop,
  onDrop,
}: WeekEventBlockProps) {
  const { event, startMinutes, durationMinutes, lane, laneCount } = item;
  const isGroup = item.groupEvents.length > 1;
  const canMove = !isGroup && event.type === 'reminder' && event.seq != null;
  const color = isGroup ? '#3478F6' : getScheduleEventColor(event, tagColors);
  const laneArea = Math.max(1, colWidth - EVENT_INSET * 2);
  const laneWidth = Math.max(10, (laneArea - EVENT_GAP * Math.max(0, laneCount - 1)) / laneCount);
  const laneLeft = EVENT_INSET + lane * (laneWidth + EVENT_GAP);
  const translateX = useSharedValue(0);
  const translateY = useSharedValue(0);
  const scrollAtDragStart = useSharedValue(0);
  const targetDay = useSharedValue(dayIndex);
  const targetMinutes = useSharedValue(startMinutes);
  const isDragging = useSharedValue(0);
  const dragScale = useSharedValue(1);
  const heightDelta = useSharedValue(0);
  const resizeDuration = useSharedValue(durationMinutes);
  const ignoreNextPress = useRef(false);
  const pressGuardTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [resizeLabel, setResizeLabel] = useState<string | null>(null);

  useEffect(() => () => {
    if (pressGuardTimer.current) clearTimeout(pressGuardTimer.current);
  }, []);

  const markDragStarted = useCallback(() => {
    ignoreNextPress.current = true;
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium).catch(() => undefined);
  }, []);

  const releasePressGuard = useCallback(() => {
    if (pressGuardTimer.current) clearTimeout(pressGuardTimer.current);
    pressGuardTimer.current = setTimeout(() => {
      ignoreNextPress.current = false;
    }, 280);
  }, []);

  const handlePress = useCallback((pressEvent: GestureResponderEvent) => {
    pressEvent.stopPropagation();
    if (ignoreNextPress.current) return;
    if (isGroup) onPressGroup();
    else {
      onSelect(event);
      if (isSelected) onPress?.(event);
    }
  }, [event, isGroup, isSelected, onPress, onPressGroup, onSelect]);

  const updateResizeLabel = useCallback((minutes: number) => {
    setResizeLabel(`${formatMinutes(startMinutes)} – ${formatMinutes(startMinutes + minutes)}`);
  }, [startMinutes]);

  const commitResize = useCallback((minutes: number) => {
    if (event.seq == null || !onResize) return;
    onResize(event, minutes);
    Haptics.selectionAsync().catch(() => undefined);
  }, [event, onResize]);

  const dragGesture = useMemo(() => Gesture.Pan()
    .enabled(canMove && Platform.OS !== 'web')
    .activateAfterLongPress(280)
    .failOffsetX([-HOLD_DRIFT_CANCEL_PX, HOLD_DRIFT_CANCEL_PX])
    .failOffsetY([-HOLD_DRIFT_CANCEL_PX, HOLD_DRIFT_CANCEL_PX])
    .blocksExternalGesture(scrollNativeGesture)
    .blocksExternalGesture(horizontalScrollNativeGesture)
    .onStart(() => {
      isDragging.value = 1;
      dragScale.value = withSpring(1.035, { damping: 20, stiffness: 280 });
      scrollAtDragStart.value = scrollOffset.value;
      targetDay.value = dayIndex;
      targetMinutes.value = startMinutes;
      runOnJS(onDragStart)(scrollOffset.value);
      runOnJS(onSelect)(event);
      runOnJS(markDragStarted)();
      runOnJS(onDragPreview)(dayIndex, startMinutes);
    })
    .onUpdate((gestureEvent) => {
      const scrollDelta = scrollOffset.value - scrollAtDragStart.value;
      const effectiveY = gestureEvent.translationY + scrollDelta;
      translateX.value = gestureEvent.translationX;
      translateY.value = effectiveY;

      const eventCenterX = columnLeft + colWidth / 2 + gestureEvent.translationX;
      const nextDay = getScheduleColumnAtX(columnWidths, eventCenterX);
      const rawMinutes = startMinutes + (effectiveY / hourHeight.value) * 60;
      const nextMinutes = Math.round(clamp(rawMinutes, 0, 1440 - SNAP_MINUTES) / SNAP_MINUTES) * SNAP_MINUTES;
      if (nextDay !== targetDay.value || nextMinutes !== targetMinutes.value) {
        targetDay.value = nextDay;
        targetMinutes.value = nextMinutes;
        runOnJS(onDragPreview)(nextDay, nextMinutes);
      }
      runOnJS(onDragEdge)(gestureEvent.absoluteY);
    })
    .onEnd(() => {
      runOnJS(onDrop)(event, targetDay.value, targetMinutes.value);
    })
    .onFinalize(() => {
      const completedDrag = isDragging.value === 1;
      isDragging.value = 0;
      translateX.value = withTiming(0, { duration: 130 });
      translateY.value = withTiming(0, { duration: 130 });
      dragScale.value = withSpring(1, { damping: 20, stiffness: 280 });
      if (completedDrag) {
        runOnJS(onDragStop)();
        runOnJS(releasePressGuard)();
      }
    }), [
      canMove, colWidth, columnLeft, columnWidths, dayIndex, dragScale, event, hourHeight, isDragging,
      horizontalScrollNativeGesture, markDragStarted, onDragEdge, onDragPreview, onDragStart, onDragStop, onDrop,
      onSelect, releasePressGuard, scrollAtDragStart, scrollNativeGesture, scrollOffset,
      startMinutes, targetDay, targetMinutes, translateX, translateY,
    ]);

  const resizeGesture = useMemo(() => Gesture.Pan()
    .enabled(!isGroup && event.seq != null && !!onResize && Platform.OS !== 'web')
    .activeOffsetY([-3, 3])
    .failOffsetX([-18, 18])
    .blocksExternalGesture(scrollNativeGesture)
    .blocksExternalGesture(horizontalScrollNativeGesture)
    .onStart(() => {
      resizeDuration.value = durationMinutes;
      runOnJS(onSelect)(event);
      runOnJS(markDragStarted)();
      runOnJS(updateResizeLabel)(durationMinutes);
    })
    .onUpdate((gestureEvent) => {
      const maximumDuration = Math.max(SNAP_MINUTES, 1440 - startMinutes);
      const rawDuration = durationMinutes + (gestureEvent.translationY / hourHeight.value) * 60;
      const nextDuration = Math.round(
        clamp(rawDuration, SNAP_MINUTES, maximumDuration) / SNAP_MINUTES,
      ) * SNAP_MINUTES;
      resizeDuration.value = nextDuration;
      heightDelta.value = ((nextDuration - durationMinutes) / 60) * hourHeight.value;
      runOnJS(updateResizeLabel)(nextDuration);
    })
    .onEnd(() => {
      runOnJS(commitResize)(resizeDuration.value);
    })
    .onFinalize(() => {
      heightDelta.value = withTiming(0, { duration: 140 });
      runOnJS(setResizeLabel)(null);
      runOnJS(releasePressGuard)();
    }), [
      commitResize, durationMinutes, event, heightDelta, hourHeight, isGroup,
      markDragStarted, onResize, onSelect, releasePressGuard, resizeDuration,
      horizontalScrollNativeGesture, scrollNativeGesture, startMinutes, updateResizeLabel,
    ]);

  const geometryStyle = useAnimatedStyle(() => ({
    top: (startMinutes / 60) * hourHeight.value,
    height: Math.max(
      EVENT_MIN_HEIGHT,
      (durationMinutes / 60) * hourHeight.value - EVENT_GAP + heightDelta.value,
    ),
    zIndex: isDragging.value ? 100 : 2,
    elevation: isDragging.value ? 10 : 0,
    opacity: isDragging.value ? 0.96 : 1,
    transform: [
      { translateX: translateX.value },
      { translateY: translateY.value },
      { scale: dragScale.value },
    ],
  }));

  const titleTypographyStyle = useAnimatedStyle(() => {
    const liveHeight = Math.max(
      EVENT_MIN_HEIGHT,
      (durationMinutes / 60) * hourHeight.value - EVENT_GAP + heightDelta.value,
    );
    const progress = getScheduleTextProgress(liveHeight, EVENT_MIN_HEIGHT, 66);
    const widthScale = Math.min(1, Math.max(0.78, laneWidth / (dense ? 92 : 112)));
    const baseSize = dense ? 8.5 : 9.5;
    const growth = dense ? 1.5 : 2.5;
    return {
      fontSize: (baseSize + progress * growth) * widthScale,
      lineHeight: (baseSize + 2 + progress * (growth + 0.5)) * widthScale,
    };
  });

  const timeTypographyStyle = useAnimatedStyle(() => {
    const liveHeight = Math.max(
      EVENT_MIN_HEIGHT,
      (durationMinutes / 60) * hourHeight.value - EVENT_GAP + heightDelta.value,
    );
    const progress = getScheduleTextProgress(liveHeight, EVENT_MIN_HEIGHT, 66);
    return {
      fontSize: 7.5 + progress * 1.5,
      lineHeight: 9 + progress * 2,
      opacity: 0.78 + progress * 0.22,
    };
  });

  const content = (
    <Animated.View
      style={[
        styles.eventBlock,
        {
          left: laneLeft,
          width: laneWidth,
          backgroundColor: isSelected ? '#FFFFFF' : getScheduleEventFillColor(color),
          borderLeftColor: color,
          borderColor: isSelected ? color : 'transparent',
          borderWidth: isSelected ? 2 : 0,
          borderLeftWidth: isSelected ? 4 : 3,
        },
        isSelected && styles.eventBlockSelected,
        geometryStyle,
      ]}
    >
      <Pressable
        style={[styles.eventPressable, dense && styles.eventPressableDense]}
        onPress={handlePress}
        android_ripple={{ color: `${color}20` }}
      >
        <View style={styles.eventTitleRow}>
          <Animated.Text
            style={[
              styles.eventText,
              dense && styles.eventTextDense,
              { color },
              event.sent && styles.eventTextDone,
              titleTypographyStyle,
            ]}
            numberOfLines={1}
          >
            {isGroup && dense ? `${item.groupEvents.length} items` : isGroup ? `${item.groupEvents.length} reminders` : event.body}
          </Animated.Text>
          {!isGroup && !dense && (event.source === 'google' || event.googleEventId) ? (
            <Ionicons
              name="logo-google"
              size={10}
              color={event.source === 'google' ? '#EA4335' : GOOGLE_BLUE}
            />
          ) : null}
        </View>
        {!dense ? (
          <Animated.Text style={[styles.eventTime, timeTypographyStyle]} numberOfLines={1}>
            {formatMinutes(startMinutes)}
          </Animated.Text>
        ) : null}
      </Pressable>
      {isSelected && !isGroup && event.seq != null && onResize ? (
        <GestureDetector gesture={resizeGesture}>
          <View
            style={styles.eventResizeHandle}
            accessibilityRole="adjustable"
            accessibilityLabel="Adjust reminder duration"
          >
            <View style={[styles.eventResizeGrip, { backgroundColor: color }]} />
          </View>
        </GestureDetector>
      ) : null}
      {resizeLabel ? (
        <View pointerEvents="none" style={styles.eventResizeLabel}>
          <Text style={styles.eventResizeLabelText}>{resizeLabel}</Text>
        </View>
      ) : null}
    </Animated.View>
  );

  if (!canMove || Platform.OS === 'web') return content;
  return <GestureDetector gesture={dragGesture}>{content}</GestureDetector>;
}

export default function WeekView({
  events,
  selectedDate,
  onSelectDate,
  onPressEvent,
  onOpenDay,
  onResizeEvent,
  onMoveEvent,
  onHorizontalOverflowChange,
  onNavigateWeek,
  tagColors,
  zoomGestureActive,
}: Props) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { width: screenWidth } = useWindowDimensions();
  const [containerWidth, setContainerWidth] = useState(0);
  const [viewportReady, setViewportReady] = useState(false);
  const [dragPreview, setDragPreview] = useState<{ dayIndex: number; minutes: number } | null>(null);
  const [localTimeOverrides, setLocalTimeOverrides] = useState<Record<number, string>>({});
  const [localDurationOverrides, setLocalDurationOverrides] = useState<Record<number, number>>({});
  const [selectedEventKey, setSelectedEventKey] = useState<string | null>(null);
  const [selectedColumnIndex, setSelectedColumnIndex] = useState<number | null>(null);
  const [columnWidths, setColumnWidths] = useState<number[]>([]);
  const [resizingColumnIndex, setResizingColumnIndex] = useState<number | null>(null);
  const dragPreviewRef = useRef<{ dayIndex: number; minutes: number } | null>(null);
  const resizingColumnIndexRef = useRef<number | null>(null);
  const availableWidth = containerWidth || screenWidth;
  const dayCount = DAYS_PER_WEEK;
  const dense = true;
  const weekDays = useMemo(() => getWeekDays(selectedDate), [selectedDate]);
  const weekStartDate = weekDays[0]?.toISODate() || '';
  const visibleStart = 0;
  const visibleDays = weekDays;
  const defaultColumnsWidth = Math.max(
    MIN_DAY_COLUMN_WIDTH * dayCount,
    availableWidth - HOUR_LABEL_WIDTH,
  );
  const equalColumnWidth = defaultColumnsWidth / dayCount;
  const resolvedColumnWidths = columnWidths.length === dayCount
    ? columnWidths
    : Array.from({ length: dayCount }, () => equalColumnWidth);
  const columnOffsets = useMemo(() => {
    let offset = 0;
    return resolvedColumnWidths.map((width) => {
      const current = offset;
      offset += width;
      return current;
    });
  }, [resolvedColumnWidths]);
  const columnsWidth = resolvedColumnWidths.reduce((total, width) => total + width, 0);
  const scheduleWidth = HOUR_LABEL_WIDTH + columnsWidth;
  const hasHorizontalOverflow = scheduleWidth > availableWidth + 1;
  const today = DateTime.local().toISODate();
  const selectedWeekContainsToday = visibleDays.some((day) => day.toISODate() === today);

  const scrollRef = useAnimatedRef<any>();
  const horizontalScrollRef = useRef<ScrollView>(null);
  const viewportRef = useRef<View>(null);
  const scrollNativeGesture = useMemo(() => Gesture.Native(), []);
  const horizontalScrollNativeGesture = useMemo(() => Gesture.Native(), []);
  const horizontalOffsetUI = useSharedValue(0);
  const horizontalMaxOffsetUI = useSharedValue(0);
  const edgeSwipeStartOffsetUI = useSharedValue(0);
  const hourHeight = useSharedValue(DEFAULT_HOUR_HEIGHT);
  const pinchStartHeight = useSharedValue(DEFAULT_HOUR_HEIGHT);
  const pinchStartScroll = useSharedValue(0);
  const pinchStartFocalY = useSharedValue(0);
  const pinchLastFocalY = useSharedValue(0);
  const pinchContentOffsetY = useSharedValue(0);
  const scrollOffset = useSharedValue(0);
  const viewportHeightUI = useSharedValue(0);
  const scrollOffsetRef = useRef(0);
  const viewportHeight = useRef(0);
  const viewportTop = useRef(0);
  const currentHourHeight = useRef(DEFAULT_HOUR_HEIGHT);
  const autoScrollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoScrollSpeed = useRef(0);
  const lastFocusKey = useRef('');
  const columnWidthsRef = useRef(resolvedColumnWidths);
  const resizeStartWidths = useRef(resolvedColumnWidths);
  const previousEqualColumnWidth = useRef(equalColumnWidth);
  const edgeNavigationLocked = useRef(false);
  const directInteractionFinishedAt = useRef(0);
  const eventInteractionEpoch = useRef(0);
  const canvasTouchStartEpoch = useRef(0);

  useEffect(() => {
    const previousBaseline = previousEqualColumnWidth.current;
    setColumnWidths((current) => {
      if (current.length !== dayCount) {
        return Array.from({ length: dayCount }, () => equalColumnWidth);
      }
      return current.map((width) => (
        equalColumnWidth + Math.max(0, width - previousBaseline)
      ));
    });
    previousEqualColumnWidth.current = equalColumnWidth;
  }, [dayCount, equalColumnWidth]);

  useEffect(() => {
    columnWidthsRef.current = resolvedColumnWidths;
  }, [resolvedColumnWidths]);

  const selectEvent = useCallback((event: CalendarEvent) => {
    eventInteractionEpoch.current += 1;
    setSelectedColumnIndex(null);
    setSelectedEventKey(eventSelectionKey(event));
  }, []);

  const clearEventSelection = useCallback(() => {
    setSelectedEventKey(null);
  }, []);

  const clearAllSelections = useCallback(() => {
    setSelectedColumnIndex(null);
    setSelectedEventKey(null);
  }, []);

  useEffect(() => {
    setSelectedColumnIndex(null);
    horizontalOffsetUI.value = 0;
    requestAnimationFrame(() => horizontalScrollRef.current?.scrollTo({ x: 0, animated: false }));
  }, [horizontalOffsetUI, weekStartDate]);

  useEffect(() => {
    onHorizontalOverflowChange?.(hasHorizontalOverflow);
    return () => onHorizontalOverflowChange?.(false);
  }, [hasHorizontalOverflow, onHorizontalOverflowChange]);

  useEffect(() => {
    const maximumOffset = Math.max(0, scheduleWidth - availableWidth);
    horizontalMaxOffsetUI.value = maximumOffset;
  }, [availableWidth, horizontalMaxOffsetUI, scheduleWidth]);

  const navigateWeekFromEdge = useCallback((direction: -1 | 1) => {
    if (
      !onNavigateWeek
      || edgeNavigationLocked.current
      || dragPreviewRef.current
      || resizingColumnIndexRef.current != null
      || Date.now() - directInteractionFinishedAt.current < 160
    ) return;
    edgeNavigationLocked.current = true;
    Haptics.selectionAsync().catch(() => undefined);
    onNavigateWeek(direction);
    setTimeout(() => {
      edgeNavigationLocked.current = false;
    }, 360);
  }, [onNavigateWeek]);

  const edgeWeekNavigationGesture = useMemo(() => Gesture.Pan()
    .enabled(hasHorizontalOverflow && !!onNavigateWeek)
    .maxPointers(1)
    .activeOffsetX([-12, 12])
    .failOffsetY([-28, 28])
    .simultaneousWithExternalGesture(horizontalScrollNativeGesture)
    .onBegin(() => {
      edgeSwipeStartOffsetUI.value = horizontalOffsetUI.value;
    })
    .onEnd((event) => {
      const maxOffset = horizontalMaxOffsetUI.value;
      const startedAtLeftEdge = edgeSwipeStartOffsetUI.value <= 2;
      const startedAtRightEdge = maxOffset > 1 && edgeSwipeStartOffsetUI.value >= maxOffset - 2;
      if (startedAtRightEdge && event.translationX <= -52) {
        runOnJS(navigateWeekFromEdge)(1);
      } else if (startedAtLeftEdge && event.translationX >= 52) {
        runOnJS(navigateWeekFromEdge)(-1);
      }
    }), [
      edgeSwipeStartOffsetUI, hasHorizontalOverflow, horizontalMaxOffsetUI,
      horizontalOffsetUI, horizontalScrollNativeGesture, navigateWeekFromEdge,
      onNavigateWeek,
    ]);

  const horizontalViewportGesture = useMemo(
    () => Gesture.Simultaneous(horizontalScrollNativeGesture, edgeWeekNavigationGesture),
    [edgeWeekNavigationGesture, horizontalScrollNativeGesture],
  );

  const beginColumnResize = useCallback((columnIndex: number) => {
    resizeStartWidths.current = [...columnWidthsRef.current];
    resizingColumnIndexRef.current = columnIndex;
    setResizingColumnIndex(columnIndex);
    Haptics.selectionAsync().catch(() => undefined);
  }, []);

  const updateColumnResize = useCallback((columnIndex: number, translationX: number) => {
    setColumnWidths(resizeScheduleColumns(
      resizeStartWidths.current,
      columnIndex,
      translationX,
      Math.max(MIN_DAY_COLUMN_WIDTH, equalColumnWidth),
    ));
  }, [equalColumnWidth]);

  const finishColumnResize = useCallback(() => {
    resizingColumnIndexRef.current = null;
    directInteractionFinishedAt.current = Date.now();
    setResizingColumnIndex(null);
    Haptics.selectionAsync().catch(() => undefined);
  }, []);

  useEffect(() => {
    setLocalTimeOverrides((current) => {
      let changed = false;
      const next = { ...current };
      events.forEach((event) => {
        if (event.seq != null && next[event.seq] === event.runAtUtc) {
          delete next[event.seq];
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [events]);

  useEffect(() => {
    setLocalDurationOverrides((current) => {
      let changed = false;
      const next = { ...current };
      events.forEach((event) => {
        if (event.seq != null && next[event.seq] === event.durationMinutes) {
          delete next[event.seq];
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [events]);

  const displayedEvents = useMemo(() => events.map((event) => {
    if (event.seq == null) return event;
    const runAtUtc = localTimeOverrides[event.seq] || event.runAtUtc;
    const durationMinutes = localDurationOverrides[event.seq] ?? event.durationMinutes;
    if (runAtUtc === event.runAtUtc && durationMinutes === event.durationMinutes) return event;
    return { ...event, runAtUtc, durationMinutes };
  }), [events, localDurationOverrides, localTimeOverrides]);

  useEffect(() => {
    if (!selectedEventKey) return;
    if (!displayedEvents.some((event) => eventSelectionKey(event) === selectedEventKey)) {
      setSelectedEventKey(null);
    }
  }, [displayedEvents, selectedEventKey]);

  const eventsByDay = useMemo(() => {
    const map: Record<string, PositionedWeekEvent[]> = {};
    const raw: Record<string, CalendarEvent[]> = {};
    visibleDays.forEach((day) => { raw[day.toISODate()!] = []; });
    displayedEvents.forEach((event) => {
      if (!event.runAtUtc) return;
      const dateKey = DateTime.fromISO(event.runAtUtc).toLocal().toISODate();
      if (dateKey && raw[dateKey]) raw[dateKey].push(event);
    });
    Object.keys(raw).forEach((dateKey) => { map[dateKey] = positionDayEvents(raw[dateKey]); });
    return map;
  }, [displayedEvents, visibleDays]);

  const autoFitColumn = useCallback((columnIndex: number) => {
    const dateKey = visibleDays[columnIndex]?.toISODate();
    if (!dateKey) return;
    const labels = (eventsByDay[dateKey] || []).flatMap((item) => (
      item.groupEvents.map((event) => event.body || '')
    ));
    const maximumWidth = Math.max(
      equalColumnWidth,
      Math.min(280, availableWidth * 0.72),
    );
    const fittedWidth = getScheduleAutoFitColumnWidth(
      labels,
      Math.max(MIN_DAY_COLUMN_WIDTH, equalColumnWidth),
      maximumWidth,
    );
    setColumnWidths((current) => {
      const next = current.length === dayCount
        ? [...current]
        : Array.from({ length: dayCount }, () => equalColumnWidth);
      next[columnIndex] = fittedWidth;
      return next;
    });
    Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success).catch(() => undefined);
  }, [availableWidth, dayCount, equalColumnWidth, eventsByDay, visibleDays]);

  const focusMinutes = useMemo(() => {
    const selectedEvents = eventsByDay[selectedDate] || [];
    if (selectedEvents.length > 0) return Math.min(...selectedEvents.map((item) => item.startMinutes));
    if (selectedDate === today) {
      const now = DateTime.local();
      return now.hour * 60 + now.minute;
    }
    const visibleEvents = Object.values(eventsByDay).flat();
    return visibleEvents.length > 0
      ? Math.min(...visibleEvents.map((item) => item.startMinutes))
      : 8 * 60;
  }, [eventsByDay, selectedDate, today]);

  const measureViewport = useCallback(() => {
    viewportRef.current?.measureInWindow?.((_x, y, _width, height) => {
      viewportTop.current = y;
      if (height > 0) viewportHeight.current = height;
    });
  }, []);

  const handleViewportLayout = useCallback((event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    setContainerWidth((current) => Math.abs(current - width) < 1 ? current : width);
  }, []);

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    const { height } = event.nativeEvent.layout;
    if (height > 0) {
      viewportHeight.current = height;
      viewportHeightUI.value = height;
      setViewportReady(true);
    }
    requestAnimationFrame(measureViewport);
  }, [measureViewport, viewportHeightUI]);

  useEffect(() => {
    const focusKey = `${selectedDate}:${visibleStart}:${dayCount}`;
    if (!viewportReady || lastFocusKey.current === focusKey || viewportHeight.current <= 0) return;
    lastFocusKey.current = focusKey;
    const maxScroll = Math.max(0, 24 * currentHourHeight.current + CONTENT_BOTTOM_PADDING - viewportHeight.current);
    const target = clamp(
      (focusMinutes / 60) * currentHourHeight.current - Math.min(180, viewportHeight.current * 0.25),
      0,
      maxScroll,
    );
    scrollOffsetRef.current = target;
    scrollOffset.value = target;
    requestAnimationFrame(() => scrollRef.current?.scrollTo?.({ y: target, animated: false }));
  }, [dayCount, focusMinutes, scrollOffset, scrollRef, selectedDate, viewportReady, visibleStart]);

  const syncScrollOffset = useCallback((y: number) => {
    scrollOffsetRef.current = y;
  }, []);

  const handleScroll = useAnimatedScrollHandler({
    onScroll: (event) => {
      const maxScroll = Math.max(
        0,
        24 * hourHeight.value + CONTENT_BOTTOM_PADDING - viewportHeightUI.value,
      );
      const nextY = Math.min(event.contentOffset.y, maxScroll);
      scrollOffset.value = nextY;
      if (event.contentOffset.y > maxScroll + 0.5) scrollTo(scrollRef, 0, maxScroll, false);
    },
    onEndDrag: (event) => {
      const maxScroll = Math.max(0, 24 * hourHeight.value + CONTENT_BOTTOM_PADDING - viewportHeightUI.value);
      runOnJS(syncScrollOffset)(Math.min(event.contentOffset.y, maxScroll));
    },
    onMomentumEnd: (event) => {
      const maxScroll = Math.max(0, 24 * hourHeight.value + CONTENT_BOTTOM_PADDING - viewportHeightUI.value);
      runOnJS(syncScrollOffset)(Math.min(event.contentOffset.y, maxScroll));
    },
  });

  const commitZoom = useCallback((nextHeight: number, nextScroll: number) => {
    currentHourHeight.current = nextHeight;
    scrollOffsetRef.current = nextScroll;
  }, []);

  const pinchGesture = useMemo(() => Gesture.Pinch()
    .enabled(Platform.OS !== 'web')
    .simultaneousWithExternalGesture(scrollNativeGesture)
    .simultaneousWithExternalGesture(horizontalScrollNativeGesture)
    .onStart((gestureEvent) => {
      if (zoomGestureActive) zoomGestureActive.value = 1;
      pinchStartHeight.value = hourHeight.value;
      pinchStartScroll.value = scrollOffset.value;
      pinchStartFocalY.value = gestureEvent.focalY;
      pinchLastFocalY.value = gestureEvent.focalY;
      pinchContentOffsetY.value = 0;
    })
    .onUpdate((gestureEvent) => {
      const nextHeight = clampScheduleScale(
        pinchStartHeight.value * gestureEvent.scale,
        MIN_HOUR_HEIGHT,
        MAX_HOUR_HEIGHT,
      );
      pinchLastFocalY.value = gestureEvent.focalY;
      const virtualScroll = getAnchoredScheduleScroll(
        pinchStartScroll.value,
        pinchStartFocalY.value,
        gestureEvent.focalY,
        pinchStartHeight.value,
        nextHeight,
        viewportHeightUI.value,
        0,
        CONTENT_BOTTOM_PADDING,
      );
      hourHeight.value = nextHeight;
      pinchContentOffsetY.value = pinchStartScroll.value - virtualScroll;
    })
    .onEnd(() => {
      const finalHeight = clampScheduleScale(hourHeight.value, MIN_HOUR_HEIGHT, MAX_HOUR_HEIGHT);
      const finalScroll = getAnchoredScheduleScroll(
        pinchStartScroll.value,
        pinchStartFocalY.value,
        pinchLastFocalY.value,
        pinchStartHeight.value,
        finalHeight,
        viewportHeightUI.value,
        0,
        CONTENT_BOTTOM_PADDING,
      );
      scrollOffset.value = finalScroll;
      scrollTo(scrollRef, 0, finalScroll, false);
      pinchContentOffsetY.value = 0;
      runOnJS(commitZoom)(finalHeight, finalScroll);
    })
    .onFinalize((_event, success) => {
      if (!success) {
        hourHeight.value = pinchStartHeight.value;
        pinchContentOffsetY.value = 0;
      }
      if (zoomGestureActive) {
        zoomGestureActive.value = withDelay(80, withTiming(0, { duration: 0 }));
      }
    }), [
      commitZoom, horizontalScrollNativeGesture, hourHeight, pinchContentOffsetY, pinchLastFocalY, pinchStartFocalY,
      pinchStartHeight, pinchStartScroll, scrollNativeGesture, scrollOffset, scrollRef,
      viewportHeightUI, zoomGestureActive,
    ]);

  const stopAutoScroll = useCallback(() => {
    autoScrollSpeed.current = 0;
    if (autoScrollTimer.current) {
      clearInterval(autoScrollTimer.current);
      autoScrollTimer.current = null;
    }
  }, []);

  useEffect(() => () => stopAutoScroll(), [stopAutoScroll]);

  const finishDrag = useCallback(() => {
    stopAutoScroll();
    dragPreviewRef.current = null;
    directInteractionFinishedAt.current = Date.now();
    setDragPreview(null);
  }, [stopAutoScroll]);

  const updateDragPreview = useCallback((dayIndex: number, minutes: number) => {
    const nextPreview = { dayIndex, minutes };
    dragPreviewRef.current = nextPreview;
    setDragPreview(nextPreview);
  }, []);

  const handleDragEdge = useCallback((absoluteY: number) => {
    const relativeY = absoluteY - viewportTop.current;
    const height = viewportHeight.current;
    if (height <= 0) return;
    let speed = 0;
    if (relativeY < EDGE_ZONE) {
      speed = -SCROLL_STEP * Math.max(0.3, 1 - relativeY / EDGE_ZONE);
    } else if (relativeY > height - EDGE_ZONE) {
      speed = SCROLL_STEP * Math.max(0.3, 1 - (height - relativeY) / EDGE_ZONE);
    }
    autoScrollSpeed.current = speed;

    if (speed !== 0 && !autoScrollTimer.current) {
      autoScrollTimer.current = setInterval(() => {
        const maxScroll = Math.max(
          0,
          24 * currentHourHeight.current + CONTENT_BOTTOM_PADDING - viewportHeight.current,
        );
        const next = clamp(scrollOffsetRef.current + autoScrollSpeed.current, 0, maxScroll);
        scrollOffsetRef.current = next;
        scrollOffset.value = next;
        scrollRef.current?.scrollTo?.({ y: next, animated: false });
      }, 16);
    } else if (speed === 0) {
      stopAutoScroll();
    }
  }, [scrollOffset, scrollRef, stopAutoScroll]);

  const handleResize = useCallback(async (event: CalendarEvent, durationMinutes: number) => {
    if (event.seq == null || !onResizeEvent) return;
    setLocalDurationOverrides((current) => ({
      ...current,
      [event.seq!]: durationMinutes,
    }));
    try {
      await onResizeEvent(event.seq, durationMinutes);
    } catch {
      setLocalDurationOverrides((current) => {
        const next = { ...current };
        delete next[event.seq!];
        return next;
      });
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error).catch(() => undefined);
    }
  }, [onResizeEvent]);

  const handleDrop = useCallback(async (
    event: CalendarEvent,
    dayIndex: number,
    minutes: number,
  ) => {
    if (event.seq == null || !onMoveEvent) return;
    const targetDay = visibleDays[dayIndex];
    if (!targetDay) return;
    const targetLocal = targetDay.startOf('day').plus({ minutes });
    const newRunAtUtc = targetLocal.toUTC().toISO();
    if (!newRunAtUtc || newRunAtUtc === event.runAtUtc) return;

    setLocalTimeOverrides((current) => ({ ...current, [event.seq!]: newRunAtUtc }));
    onSelectDate(targetDay.toISODate()!);
    Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success).catch(() => undefined);
    try {
      await onMoveEvent(event.seq, newRunAtUtc);
    } catch {
      setLocalTimeOverrides((current) => {
        const next = { ...current };
        delete next[event.seq!];
        return next;
      });
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error).catch(() => undefined);
    }
  }, [onMoveEvent, onSelectDate, visibleDays]);

  const canvasOffsetStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: pinchContentOffsetY.value }],
  }));
  const fixedHoursOffsetStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: -scrollOffset.value + pinchContentOffsetY.value }],
  }));

  const scrollView = (
    <AnimatedScrollView
      ref={scrollRef}
      style={styles.scrollView}
      onScroll={handleScroll}
      scrollEventThrottle={16}
      showsVerticalScrollIndicator={false}
      bounces={false}
      overScrollMode="never"
      nestedScrollEnabled
      keyboardShouldPersistTaps="handled"
    >
      <View style={[styles.scrollExtent, { height: 24 * MAX_HOUR_HEIGHT + CONTENT_BOTTOM_PADDING }]}>
        <Animated.View
          onTouchStart={() => {
            canvasTouchStartEpoch.current = eventInteractionEpoch.current;
          }}
          onTouchEnd={() => {
            const interactionAtStart = canvasTouchStartEpoch.current;
            setTimeout(() => {
              if (
                eventInteractionEpoch.current === interactionAtStart
                && !dragPreviewRef.current
                && resizingColumnIndexRef.current == null
              ) {
                clearAllSelections();
              }
            }, 0);
          }}
          style={[
            styles.canvas,
            { width: HOUR_LABEL_WIDTH + columnsWidth, height: 24 * MAX_HOUR_HEIGHT },
            canvasOffsetStyle,
          ]}
        >
          {Array.from({ length: 24 }, (_, hour) => (
            <HourMarker key={hour} hour={hour} hourHeight={hourHeight} styles={styles} />
          ))}

          <View
            style={[
              styles.columns,
              { left: HOUR_LABEL_WIDTH, width: columnsWidth, height: 24 * MAX_HOUR_HEIGHT },
            ]}
          >
            {visibleDays.map((day, dayIndex) => {
              const dateKey = day.toISODate()!;
              const isColumnSelected = selectedColumnIndex === dayIndex;
              return (
                <View
                  key={dateKey}
                  style={[
                    styles.dayColumn,
                    { width: resolvedColumnWidths[dayIndex] },
                    isColumnSelected && styles.dayColumnSelected,
                  ]}
                >
                  {isColumnSelected ? <View pointerEvents="none" style={styles.selectedColumnOutline} /> : null}
                  {(eventsByDay[dateKey] || []).map((item) => (
                    <WeekEventBlock
                      key={item.event.seq != null
                        ? `reminder-${item.event.seq}`
                        : `event-${item.event.runAtUtc}-${item.event.body}`}
                      item={item}
                      dayIndex={dayIndex}
                      colWidth={resolvedColumnWidths[dayIndex]}
                      columnLeft={columnOffsets[dayIndex]}
                      columnWidths={resolvedColumnWidths}
                      dense={dense}
                      isSelected={selectedEventKey === eventSelectionKey(item.event)}
                      hourHeight={hourHeight}
                      scrollOffset={scrollOffset}
                      scrollNativeGesture={scrollNativeGesture}
                      horizontalScrollNativeGesture={horizontalScrollNativeGesture}
                      styles={styles}
                      tagColors={tagColors}
                      onPress={onPressEvent}
                      onSelect={selectEvent}
                      onResize={onResizeEvent ? handleResize : undefined}
                      onPressGroup={() => {
                        clearAllSelections();
                        if (onOpenDay) onOpenDay(dateKey);
                        else onSelectDate(dateKey);
                      }}
                      onDragStart={syncScrollOffset}
                      onDragPreview={updateDragPreview}
                      onDragEdge={handleDragEdge}
                      onDragStop={finishDrag}
                      onDrop={handleDrop}
                    />
                  ))}
                </View>
              );
            })}
          </View>
          {selectedWeekContainsToday ? <NowLine hourHeight={hourHeight} styles={styles} /> : null}
        </Animated.View>
      </View>
    </AnimatedScrollView>
  );

  const scrollHost = Platform.OS === 'web'
    ? scrollView
    : <GestureDetector gesture={scrollNativeGesture}>{scrollView}</GestureDetector>;

  const body = (
    <View ref={viewportRef} style={styles.body} onLayout={handleLayout}>
      {scrollHost}
      {dragPreview ? (
        <View style={styles.dragPreview} pointerEvents="none">
          <Text style={styles.dragPreviewText}>
            {visibleDays[dragPreview.dayIndex]?.toFormat('ccc d')} · {formatMinutes(dragPreview.minutes)}
          </Text>
        </View>
      ) : null}
    </View>
  );

  const scheduleContent = (
    <View style={[styles.horizontalContent, { width: scheduleWidth }]}>
      <View style={styles.stickyHeader}>
        <View style={styles.headerGutter} />
        <View style={[styles.headerColumns, { width: columnsWidth }]}>
          {visibleDays.map((day, dayIndex) => {
            const dateKey = day.toISODate()!;
            const isToday = dateKey === today;
            const isColumnSelected = selectedColumnIndex === dayIndex;
            return (
              <Pressable
                key={dateKey}
                onPress={() => {
                  clearEventSelection();
                  setSelectedColumnIndex(dayIndex);
                }}
                style={[
                  styles.dayHeader,
                  { width: resolvedColumnWidths[dayIndex] },
                  isColumnSelected && styles.dayHeaderSelected,
                  resizingColumnIndex === dayIndex && styles.dayHeaderResizing,
                ]}
              >
                <Text style={[styles.dayName, isColumnSelected && styles.dayNameSelected]}>
                  {day.toFormat(dense ? 'ccc' : 'cccc')}
                </Text>
                <View style={[styles.dayNumber, isToday && styles.dayNumberToday]}>
                  <Text
                    style={[
                      styles.dayNumberText,
                      isColumnSelected && styles.dayNumberTextSelected,
                      isToday && styles.dayNumberTextToday,
                    ]}
                  >
                    {day.day}
                  </Text>
                </View>
                {isColumnSelected ? (
                  <ColumnResizeHandle
                    columnIndex={dayIndex}
                    styles={styles}
                    active={resizingColumnIndex === dayIndex}
                    atTrailingEdge={dayIndex === dayCount - 1}
                    horizontalScrollNativeGesture={horizontalScrollNativeGesture}
                    onResizeStart={beginColumnResize}
                    onResize={updateColumnResize}
                    onResizeEnd={finishColumnResize}
                    onAutoFit={autoFitColumn}
                  />
                ) : null}
              </Pressable>
            );
          })}
        </View>
      </View>
      {Platform.OS === 'web'
        ? body
        : <GestureDetector gesture={pinchGesture}>{body}</GestureDetector>}
    </View>
  );

  const horizontalScroll = (
    <ScrollView
      ref={horizontalScrollRef}
      horizontal
      style={styles.horizontalScroll}
      contentContainerStyle={styles.horizontalScrollContent}
      showsHorizontalScrollIndicator={hasHorizontalOverflow}
      scrollEnabled={hasHorizontalOverflow}
      directionalLockEnabled
      nestedScrollEnabled
      bounces={false}
      overScrollMode="never"
      keyboardShouldPersistTaps="handled"
      onScroll={(event) => {
        horizontalOffsetUI.value = event.nativeEvent.contentOffset.x;
      }}
      scrollEventThrottle={16}
    >
      {scheduleContent}
    </ScrollView>
  );

  return (
    <View style={styles.viewport} onLayout={handleViewportLayout}>
      {Platform.OS === 'web'
        ? horizontalScroll
        : (
          <GestureDetector gesture={horizontalViewportGesture}>
            {horizontalScroll}
          </GestureDetector>
        )}
      <View pointerEvents="none" style={styles.fixedHeaderGutter} />
      <View pointerEvents="none" style={styles.fixedHourGutter}>
        <Animated.View
          style={[
            styles.fixedHourCanvas,
            { height: 24 * MAX_HOUR_HEIGHT },
            fixedHoursOffsetStyle,
          ]}
        >
          {Array.from({ length: 24 }, (_, hour) => (
            <FixedHourLabel key={hour} hour={hour} hourHeight={hourHeight} styles={styles} />
          ))}
        </Animated.View>
      </View>
    </View>
  );
}

function createStyles(colors: ThemeColors) {
  return StyleSheet.create({
    viewport: {
      flex: 1,
      position: 'relative',
      overflow: 'hidden',
      backgroundColor: colors.background,
    },
    horizontalScroll: {
      flex: 1,
      backgroundColor: colors.background,
    },
    horizontalScrollContent: {
      flexGrow: 1,
      alignItems: 'stretch',
    },
    horizontalContent: {
      flex: 1,
      minHeight: '100%',
      backgroundColor: colors.background,
    },
    fixedHeaderGutter: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: HOUR_LABEL_WIDTH,
      height: HEADER_HEIGHT,
      zIndex: 180,
      elevation: 12,
      backgroundColor: colors.background,
      borderRightWidth: StyleSheet.hairlineWidth,
      borderRightColor: colors.border,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border,
    },
    fixedHourGutter: {
      position: 'absolute',
      top: HEADER_HEIGHT,
      bottom: 0,
      left: 0,
      width: HOUR_LABEL_WIDTH,
      zIndex: 170,
      elevation: 10,
      overflow: 'hidden',
      backgroundColor: colors.background,
      borderRightWidth: StyleSheet.hairlineWidth,
      borderRightColor: colors.border,
    },
    fixedHourCanvas: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
    },
    fixedHourLabel: {
      position: 'absolute',
      top: -10,
      left: 0,
      right: 0,
      height: 20,
      justifyContent: 'center',
    },
    fixedHourLabelText: {
      paddingRight: 8,
      textAlign: 'right',
      fontSize: 10,
      fontWeight: '500',
      color: colors.textSecondary,
    },
    body: {
      flex: 1,
      position: 'relative',
      overflow: 'hidden',
      backgroundColor: colors.background,
    },
    scrollView: {
      flex: 1,
      backgroundColor: colors.background,
    },
    scrollExtent: {
      position: 'relative',
      minWidth: '100%',
    },
    canvas: {
      position: 'absolute',
      left: 0,
      top: 0,
    },
    stickyHeader: {
      height: HEADER_HEIGHT,
      flexDirection: 'row',
      zIndex: 20,
      elevation: 2,
      backgroundColor: colors.background,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border,
    },
    headerGutter: {
      width: HOUR_LABEL_WIDTH,
      height: HEADER_HEIGHT,
      backgroundColor: colors.background,
    },
    headerColumns: {
      height: HEADER_HEIGHT,
      flexDirection: 'row',
    },
    dayHeader: {
      height: HEADER_HEIGHT,
      position: 'relative',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1,
      borderLeftWidth: StyleSheet.hairlineWidth,
      borderLeftColor: colors.border,
      backgroundColor: colors.background,
      overflow: 'visible',
    },
    dayHeaderSelected: {
      backgroundColor: colors.primaryLight,
      borderLeftWidth: 2,
      borderLeftColor: colors.primary,
      borderRightWidth: 2,
      borderRightColor: colors.primary,
      zIndex: 70,
      elevation: 8,
    },
    dayHeaderResizing: {
      backgroundColor: `${colors.primary}20`,
    },
    columnResizeHitArea: {
      position: 'absolute',
      top: 0,
      right: -10,
      bottom: 0,
      width: 20,
      zIndex: 120,
      elevation: 24,
      alignItems: 'center',
      justifyContent: 'center',
      ...(Platform.OS === 'web' ? { cursor: 'col-resize' } : null),
    } as any,
    columnResizeHitAreaActive: {
      width: 26,
      right: -13,
    },
    columnResizeHitAreaTrailing: {
      right: 0,
    },
    columnResizeLine: {
      position: 'absolute',
      top: 0,
      bottom: 0,
      width: 3,
      borderRadius: 2,
      backgroundColor: colors.primary,
    },
    columnResizeLineTrailing: {
      transform: [{ translateX: 10 }],
    },
    columnResizeKnob: {
      width: 20,
      height: 20,
      borderRadius: 10,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.primary,
      borderWidth: 2,
      borderColor: '#FFFFFF',
    },
    dayName: {
      fontSize: 9,
      lineHeight: 11,
      color: colors.textSecondary,
      fontWeight: '700',
      textTransform: 'uppercase',
    },
    dayNameSelected: {
      color: colors.primary,
    },
    dayNumber: {
      minWidth: 24,
      height: 24,
      borderRadius: 12,
      paddingHorizontal: 3,
      alignItems: 'center',
      justifyContent: 'center',
    },
    dayNumberToday: {
      backgroundColor: colors.primary,
    },
    dayNumberText: {
      fontSize: 12,
      lineHeight: 15,
      color: colors.text,
      fontWeight: '700',
    },
    dayNumberTextSelected: {
      color: colors.primary,
    },
    dayNumberTextToday: {
      color: '#FFFFFF',
    },
    hourMarker: {
      position: 'absolute',
      left: 0,
      right: 0,
      top: -10,
      height: 20,
      flexDirection: 'row',
      alignItems: 'center',
      zIndex: 1,
    },
    hourText: {
      width: HOUR_LABEL_WIDTH,
      paddingRight: 8,
      textAlign: 'right',
      fontSize: 10,
      fontWeight: '500',
      color: colors.textSecondary,
    },
    hourLine: {
      flex: 1,
      height: StyleSheet.hairlineWidth,
      backgroundColor: colors.border,
    },
    columns: {
      position: 'absolute',
      top: 0,
      flexDirection: 'row',
      overflow: 'visible',
    },
    dayColumn: {
      position: 'relative',
      height: '100%',
      overflow: 'visible',
      borderLeftWidth: StyleSheet.hairlineWidth,
      borderLeftColor: colors.border,
    },
    dayColumnSelected: {
      backgroundColor: `${colors.primary}06`,
    },
    selectedColumnOutline: {
      ...StyleSheet.absoluteFillObject,
      zIndex: 40,
      borderLeftWidth: 2,
      borderRightWidth: 2,
      borderColor: colors.primary,
      opacity: 0.7,
    },
    eventBlock: {
      position: 'absolute',
      borderLeftWidth: 3,
      borderRadius: 8,
      borderTopLeftRadius: 3,
      borderBottomLeftRadius: 3,
      overflow: 'hidden',
    },
    eventBlockSelected: {
      zIndex: 60,
      elevation: 7,
      overflow: 'visible',
      shadowColor: '#000000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.22,
      shadowRadius: 6,
    },
    eventResizeHandle: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: -12,
      height: 24,
      zIndex: 90,
      elevation: 12,
      alignItems: 'center',
      justifyContent: 'center',
    },
    eventResizeGrip: {
      width: 34,
      height: 5,
      borderRadius: 3,
      borderWidth: 1,
      borderColor: '#FFFFFF',
      shadowColor: '#000000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.2,
      shadowRadius: 2,
    },
    eventResizeLabel: {
      position: 'absolute',
      left: 2,
      right: 2,
      bottom: -34,
      zIndex: 100,
      alignItems: 'center',
    },
    eventResizeLabelText: {
      paddingHorizontal: 7,
      paddingVertical: 3,
      borderRadius: 10,
      overflow: 'hidden',
      backgroundColor: colors.primary,
      color: '#FFFFFF',
      fontSize: 9,
      lineHeight: 11,
      fontWeight: '800',
    },
    eventPressable: {
      flex: 1,
      minHeight: EVENT_MIN_HEIGHT,
      paddingHorizontal: 7,
      paddingVertical: 4,
      justifyContent: 'flex-start',
    },
    eventPressableDense: {
      paddingHorizontal: 3,
      paddingVertical: 2,
    },
    eventTitleRow: {
      minWidth: 0,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 3,
    },
    eventText: {
      flex: 1,
      minWidth: 0,
      fontSize: 11,
      lineHeight: 14,
      fontWeight: '700',
    },
    eventTextDense: {
      fontSize: 9,
      lineHeight: 11,
      fontWeight: '600',
    },
    eventTextDone: {
      textDecorationLine: 'line-through',
    },
    eventTime: {
      marginTop: 1,
      fontSize: 9,
      lineHeight: 11,
      color: colors.textSecondary,
      fontWeight: '500',
    },
    nowLineWrap: {
      position: 'absolute',
      left: HOUR_LABEL_WIDTH - 4,
      right: 0,
      top: 0,
      height: 2,
      flexDirection: 'row',
      alignItems: 'center',
      zIndex: 50,
    },
    nowDot: {
      width: 8,
      height: 8,
      borderRadius: 4,
      backgroundColor: '#EA4335',
    },
    nowLine: {
      flex: 1,
      height: 1.5,
      backgroundColor: '#EA4335',
    },
    dragPreview: {
      position: 'absolute',
      top: 8,
      alignSelf: 'center',
      zIndex: 200,
      elevation: 20,
      paddingHorizontal: 12,
      paddingVertical: 7,
      borderRadius: 16,
      backgroundColor: colors.primary,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 3 },
      shadowOpacity: 0.2,
      shadowRadius: 5,
    },
    dragPreviewText: {
      color: '#FFFFFF',
      fontSize: 12,
      fontWeight: '700',
    },
  });
}
